import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { ConnectorItemEntity } from './connector-item.entity';

export interface IConnectorListExtra
	extends IListColumnConfig<ConnectorItemEntity> {
	isFixed?: boolean;
	isSelectable?: boolean;
	isSearchable?: boolean;
	isInGroup?: boolean;
	isVisible?: boolean;
	canBeHidden?: boolean;
	canBeReordered?: boolean;
	canBeChosen?: boolean;
	format?: string;
	visibleIndex?: number;
	visible?: boolean;
	hasHeaderFilter?: boolean;
	width?: string | number;
	filterValue?: Array<string | number>;
	filterValues?: Array<string | number>;
	sortIndex?: number;
	sortOrder?: 'asc' | 'desc';
}

interface IConnectorListEntity extends IListAttrs<ConnectorItemEntity> {
	availableColumns: Map<string, IConnectorListExtra>;
}

const entityDefaults: IConnectorListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

export class ConnectorListEntity
	implements
		IConnectorListEntity,
		IListOperations<ConnectorListEntity, ConnectorItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(
		availableCols: Array<IConnectorListExtra>
	): ConnectorListEntity {
		const inst = new ConnectorListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IConnectorListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<ConnectorItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(
		elems: Map<string, ConnectorItemEntity>
	): ConnectorListEntity {
		return produce(this, (draft: ConnectorListEntity) => {
			draft.elements = elems;
		});
	}

	public updateItem(entityId: number): ConnectorListEntity {
		return produce(this, (draft: ConnectorListEntity) => {
			const maybeFound = draft.elements.get(`${entityId}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(`${entityId}`, maybeFound);
		});
	}

	public extractUniqTags(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, connectorItem) => {
			if (!connectorItem.tags) {
				return acc;
			}
			return Set([
				...acc.toArray(),
				...connectorItem.tags
					.toList()
					.toArray()
					.map((l) => l.label),
			]);
		}, Set<string>(startValues));
	}

	public extractModifiers(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, connectorItem) => {
			if (
				connectorItem.updatedByAvatar &&
				connectorItem.updatedByAvatar?.name
			) {
				acc = acc.add(connectorItem.updatedByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractActionCreators(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, connectorItem) => {
			if (connectorItem.createdByAvatar && connectorItem.createdByAvatar.name) {
				acc = acc.add(connectorItem.createdByAvatar.name);
			}
			return acc;
		}, Set<string>(startValues));
	}
}
